// Greenhold <ParchmentPost> — the parchment card treatment for The Wire.
//
// Epic 8 design pass (2026-08), ported from the design mirror
// (design/claude-design/ParchmentPost.jsx). A parchment-gradient ground
// with the same wear treatment as <SealedDossier>, a mono-uppercase
// author/subject row, ink-on-parchment body, and the honesty-seal footer
// rendered as a printed document line (provenance left · time right).
// Sits on the dark wire ground; no hover motion (feed-calm rule).
// Successor to the forest-dark GreenholdFrame for feed posts.
//
// This package has no GreenholdFrame, so the `Provenance` vocabulary is
// defined here (the design .d.ts imported it from a GreenholdFrame that
// only exists app-side). Feed adoption is a SEPARATE agent's job — this
// file only adds the component + its gallery specimen.

import type { CSSProperties, ReactNode } from "react";

/** Closed preposition vocabulary for the printed provenance footer. */
export type Provenance = "posted-by" | "about" | "via" | "editorial";

export interface ParchmentPostProps {
  /** Author identity, top-left (mono uppercase) — whoever occupies it is
   *  claiming the words. */
  author?: ReactNode;
  /** Subject, top-right (mono uppercase, fainter). */
  subject?: ReactNode;
  /** Closed preposition vocabulary for the printed footer. @default 'editorial' */
  provenance?: Provenance;
  /** Handle/entity after the preposition, e.g. "@nova". Ignored for 'editorial'. */
  provenanceName?: string;
  /** Relative time, printed footer-right. */
  time?: string;
  /** Optional headline (editorial posts) — Inter 600 in ink-900. */
  title?: ReactNode;
  /** Stronger gold hairline (0.55 vs 0.35) — pair with PinnedChip above. @default false */
  pinned?: boolean;
  /** Pins the wear variant (0-2). Omitted = cycles so neighbors differ. */
  seed?: number;
  children?: ReactNode;
  style?: CSSProperties;
}

const PREFIX: Record<Provenance, string> = {
  "posted-by": "Posted by",
  about: "About",
  via: "via",
  editorial: "Editorial · Greenhold",
};

// Three wear variants — the hand-tuned radial pairs from the design
// session. Cycled by module counter (or pinned via the seed prop) so
// adjacent cards never share identical aging.
const WEAR = [
  "radial-gradient(ellipse at 18% 12%, rgba(122,90,29,0.08), transparent 40%), radial-gradient(ellipse at 85% 78%, rgba(122,90,29,0.10), transparent 45%)",
  "radial-gradient(ellipse at 80% 15%, rgba(122,90,29,0.09), transparent 42%), radial-gradient(ellipse at 15% 85%, rgba(122,90,29,0.10), transparent 45%)",
  "radial-gradient(ellipse at 22% 80%, rgba(122,90,29,0.09), transparent 42%), radial-gradient(ellipse at 88% 20%, rgba(122,90,29,0.08), transparent 40%)",
];
let ppSeq = 0;

/**
 * The parchment card treatment for The Wire: parchment gradient with the
 * SealedDossier wear treatment, ink-on-parchment body, honesty-seal
 * footer as a printed document line. Renders on the dark wire ground; no
 * hover motion. Successor to the forest-dark GreenholdFrame for feed
 * posts.
 */
export function ParchmentPost({
  author,
  subject,
  provenance = "editorial",
  provenanceName,
  time,
  title,
  pinned = false,
  seed,
  children,
  style,
}: ParchmentPostProps) {
  const wear = WEAR[Math.abs(seed ?? ppSeq++) % WEAR.length];
  const chipText =
    provenance === "editorial"
      ? PREFIX.editorial
      : `${PREFIX[provenance] || PREFIX.editorial} ${provenanceName || ""}`.trim();
  const mono: CSSProperties = {
    fontFamily: "var(--gh-font-mono, 'JetBrains Mono', ui-monospace, monospace)",
    fontSize: "0.6rem",
    textTransform: "uppercase",
    letterSpacing: "0.14em",
  };
  return (
    <div
      style={{
        position: "relative",
        background:
          "linear-gradient(115deg, var(--gh-parchment-hi, #ece4cc) 0, var(--gh-parchment, #e7dec4) 55%, var(--gh-parchment-lo, #ddd0ad) 100%)",
        border: `1px solid rgba(195,146,53,${pinned ? 0.55 : 0.35})`,
        borderRadius: "var(--gh-radius-md, 6px)",
        padding: "18px 22px",
        overflowWrap: "anywhere",
        fontFamily:
          "var(--gh-font-body, system-ui, -apple-system, 'Segoe UI', sans-serif)",
        boxShadow: "0 3px 10px rgba(0,0,0,0.4)",
        ...style,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "var(--gh-radius-md, 6px)",
          pointerEvents: "none",
          background: wear,
          boxShadow: "inset 0 0 18px rgba(58,47,28,0.18)",
        }}
      />
      {(author || subject) && (
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span style={{ ...mono, color: "rgba(58,47,28,0.7)" }}>{author}</span>
          {subject && (
            <span style={{ ...mono, color: "rgba(58,47,28,0.55)" }}>
              {subject}
            </span>
          )}
        </div>
      )}
      {title && (
        <p
          style={{
            fontFamily:
              "var(--gh-font-heading, 'Inter', system-ui, sans-serif)",
            color: "var(--gh-ink-900, #1f1408)",
            fontSize: "1.15rem",
            fontWeight: 600,
            margin: "12px 0 6px",
          }}
        >
          {title}
        </p>
      )}
      <div
        style={{
          color: "var(--gh-ink-800, #2e2310)",
          fontSize: "0.92rem",
          lineHeight: 1.55,
          margin: title ? 0 : "12px 0 0",
        }}
      >
        {children}
      </div>
      <div
        style={{
          borderTop: "1px solid rgba(58,47,28,0.25)",
          marginTop: 14,
          paddingTop: 8,
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          fontFamily: "var(--gh-font-mono, 'JetBrains Mono', ui-monospace, monospace)",
          fontSize: "0.6rem",
          letterSpacing: "0.08em",
          color: "rgba(58,47,28,0.6)",
        }}
      >
        <span>{chipText}</span>
        {time && <span>{time}</span>}
      </div>
    </div>
  );
}
